import React, { useCallback } from "react";

import {
  PageIndex,
  ProgressPoint,
} from "../CreateUpdatePlanFlow/reducer/types";
import LogoWordmark from "@src/multiCustodian/theme/LogoWordmark";
import WizardProgressBar from "@src/sharedComponents/WizardProgressBar/WizardProgressBar";
import { assertUnreachable } from "@src/utils/assertUnreachable";

import styles from "./ProgressBar.module.css";

type ProgressPointIndex = Exclude<PageIndex, 6 | 7>;

const isPageIndex = (idx: number): idx is PageIndex => {
  return idx >= 0 && idx <= 7;
};

const isProgressPointIndex = (idx: number): idx is ProgressPointIndex => {
  return idx >= 0 && idx <= 5; // step pages (excludes first overview page and last summary page)
};

interface Props {
  progressState: ProgressPoint[];
  setPage: (pageNumber: PageIndex) => void;
}

const ProgressBar = ({ progressState, setPage }: Props): JSX.Element => {
  const canClickTo = useCallback(
    (idx: number) => {
      if (isProgressPointIndex(idx)) {
        switch (idx) {
          case 0:
            return true;
          case 1:
            return (
              progressState[0].state === "Complete" ||
              progressState[1].state === "Complete"
            );
          case 2:
            return (
              progressState[1].state === "Complete" ||
              progressState[2].state === "Complete"
            );
          case 3:
            return (
              progressState[2].state === "Complete" ||
              progressState[3].state === "Complete"
            );
          case 4:
            return (
              progressState[3].state === "Complete" ||
              progressState[4].state === "Complete"
            );
          case 5:
            return (
              progressState[4].state === "Complete" ||
              progressState[5].state === "Complete"
            );
          default:
            return assertUnreachable(idx);
        }
      }

      return false;
    },
    [progressState]
  );

  const onSetPage = (idx: number) => {
    const index = idx + 1;
    if (isPageIndex(index) && canClickTo(idx)) {
      setPage(index);
    }
  };

  return (
    <div className={styles.container}>
      <LogoWordmark className={styles.fartherLogo} />

      <div className={styles.progressBarCenteringContainer}>
        <WizardProgressBar
          className={styles.progressBar}
          progressState={progressState}
          setPage={onSetPage}
          canClickTo={canClickTo}
        />
      </div>

      <div className={styles.end} />
    </div>
  );
};

export default ProgressBar;
